/**
 * Active device sessions — lists the current user's logged-in devices from
 * GET /auth/sessions, lets them revoke any non-current session, and offers
 * "Sign out everywhere" (logoutAll). Shared across all user types.
 *
 * The backend parses the stored User-Agent into browser / OS / device-type /
 * device-model and normalises the IP (flagging loopback/private as local), so
 * each row shows a robust, human-readable device summary rather than a bare
 * "Desktop · ::1". A per-row "Details" toggle reveals the raw User-Agent.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  TbDeviceMobile, TbDeviceTablet, TbDeviceLaptop, TbDeviceTv,
  TbDeviceWatch, TbDeviceGamepad, TbDeviceDesktop,
} from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { ROUTES } from '../../constants/constants';
import { Pill, GhostButton } from './ProfilePrimitives';
import Button from '../common/Button';

const BORDER = '#ddd6c6';
const TEXT = '#08111f';
const MUTED = '#7a7060';
const FAINT = '#9a8f7c';

function deviceIcon(type) {
  if (type === 'Mobile') return TbDeviceMobile;
  if (type === 'Tablet') return TbDeviceTablet;
  if (type === 'Desktop') return TbDeviceLaptop;
  if (type === 'Smarttv') return TbDeviceTv;
  if (type === 'Wearable') return TbDeviceWatch;
  if (type === 'Console') return TbDeviceGamepad;
  return TbDeviceDesktop;
}

function relativeTime(value) {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Bold first line: "Chrome 120 · Windows 11", degrading gracefully.
function deviceTitle(s) {
  const parts = [s.browser, s.os].filter(Boolean);
  if (parts.length) return parts.join(' · ');
  return s.label || s.deviceType || 'Unknown device';
}

export default function SessionsList() {
  const navigate = useNavigate();
  const { logoutAll } = useAuth();
  const { showToast } = useUI();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAPI.getSessions();
      setSessions(res?.data?.sessions || []);
    } catch (err) {
      showToast(err?.message || 'Could not load sessions', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    setBusyId(id);
    try {
      await authAPI.revokeSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      showToast('Session signed out', 'success');
    } catch (err) {
      showToast(err?.message || 'Could not revoke session', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const signOutEverywhere = async () => {
    if (!window.confirm('Sign out of all devices, including this one? You will need to log in again.')) return;
    await logoutAll();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  if (loading) {
    return <div className="text-[12px]" style={{ color: MUTED }}>Loading sessions…</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12.5px] font-semibold" style={{ color: TEXT }}>
          Active sessions ({sessions.length})
        </div>
        <GhostButton onClick={signOutEverywhere}>Sign out everywhere</GhostButton>
      </div>

      <div className="space-y-2">
        {sessions.map((s) => {
          const ipText = s.isLocalIp ? 'Local network' : (s.ipAddress || 'Unknown IP');
          const deviceDesc = s.deviceModel || s.deviceType || 'Device';
          const expanded = expandedId === s.id;
          return (
            <div
              key={s.id}
              className="flex items-start gap-3 px-3 py-2.5 rounded-lg border"
              style={{ borderColor: BORDER, background: s.isCurrent ? '#f0f7f6' : '#fff' }}
            >
              <span className="leading-none mt-[2px] shrink-0" style={{ color: '#145f5f' }}>
                {(() => { const DeviceIco = deviceIcon(s.deviceType); return <DeviceIco size={18} aria-hidden="true" />; })()}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[12.5px] font-semibold" style={{ color: TEXT }}>
                    {deviceTitle(s)}
                  </span>
                  {s.isCurrent && <Pill tone="teal">This device</Pill>}
                </div>

                <div className="text-[11px] mt-[2px]" style={{ color: MUTED }}>
                  {deviceDesc} · {ipText}
                </div>

                <div className="text-[10.5px] mt-[1px]" style={{ color: FAINT }}>
                  {s.signedInAt && <>Signed in {formatDate(s.signedInAt)} · </>}
                  active {relativeTime(s.lastActive)}
                </div>

                {s.userAgent && (
                  <>
                    <button
                      type="button"
                      onClick={() => setExpandedId(expanded ? null : s.id)}
                      className="mt-[5px] text-[10.5px] font-semibold"
                      style={{ color: '#145f5f' }}
                    >
                      {expanded ? 'Hide details ▴' : 'Details ▾'}
                    </button>
                    {expanded && (
                      <div
                        className="mt-1.5 px-2.5 py-2 rounded-md text-[10.5px] break-all"
                        style={{
                          background: '#f8f5ef',
                          border: `1px solid ${BORDER}`,
                          color: MUTED,
                          fontFamily: "'JetBrains Mono', monospace",
                        }}
                      >
                        {s.userAgent}
                      </div>
                    )}
                  </>
                )}
              </div>

              {!s.isCurrent && (
                <Button
                  variant="danger"
                  size="sm"
                  className="shrink-0"
                  onClick={() => revoke(s.id)}
                  loading={busyId === s.id}
                >
                  Sign out
                </Button>
              )}
            </div>
          );
        })}
        {sessions.length === 0 && (
          <div className="text-[12px]" style={{ color: MUTED }}>No active sessions found.</div>
        )}
      </div>
    </div>
  );
}
